import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";

const BASE = "https://api.livestockify.com/api/admin";

export default function EditDevices() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [devices, setDevices] = useState([]);
  const [dosingUnits, setDosingUnits] = useState([]);
  const [loading, setLoading] = useState(true);

  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deviceRes, dosingRes] = await Promise.all([
          axios.get(`${BASE}/users/uuid/${id}/devices`),
          axios.get(`${BASE}/users/${id}/dosing-units`),
        ]);

        setDevices(deviceRes.data || []);
        setDosingUnits(dosingRes.data || []);
      } catch (err) {
        console.error("Fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  /* ================= UPDATE STATE ================= */
  const updateDevice = (index, key, value) => {
    setDevices((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [key]: value } : d))
    );
  };

  const updateDosing = (index, key, value) => {
    setDosingUnits((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [key]: value } : d))
    );
  };

  /* ================= ADD ================= */
  const addDevice = () => {
    setDevices((prev) => [
      ...prev,
      { deviceName: "", deviceId: "", deviceInfo: "" },
    ]);
  };

  const addDosing = () => {
    setDosingUnits((prev) => [
      ...prev,
      {
        deviceId: "",
        solutionCapacity: "",
        dripAmount: "",
        deviceInfo: "",
      },
    ]);
  };

  /* ================= DELETE ================= */
  const deleteDevice = async (deviceId, index) => {
    if (deviceId) {
      await axios.delete(`${BASE}/users/uuid/${id}/devices/${deviceId}`);
    }
    setDevices((prev) => prev.filter((_, i) => i !== index));
  };

  const deleteDosing = async (dosingId, index) => {
    if (dosingId) {
      await axios.delete(`${BASE}/users/${id}/dosing-units/${dosingId}`);
    }
    setDosingUnits((prev) => prev.filter((_, i) => i !== index));
  };

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      await Promise.all([
        ...devices.map((d) =>
          axios.put(`${BASE}/users/uuid/${id}/devices/${d.id}`, d)
        ),
        ...dosingUnits.map((d) =>
          axios.put(`${BASE}/users/${id}/dosing-units/${d.id}`, d)
        ),
      ]);

      navigate("/all-users");
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />

      <div className="flex-1 bg-white rounded-l-2xl px-6 py-8 overflow-y-auto h-screen">
        {/* ================= TOP BAR ================= */}
        <div className="flex justify-end mb-6">
          <button
            onClick={() => navigate("/users")}
            className="text-sm text-gray-500 hover:text-green-600"
          >
            Skip &gt;
          </button>
        </div>

        {/* ================= STEPPER ================= */}
        <div className="flex justify-center mb-12">
          <div className="relative flex items-center w-full max-w-md">
  {/* LINE: from circle center to circle center */}
  <div className="absolute left-1/4 right-1/4 top-[6px] h-[3px] bg-green-600" />

  {["Edit User", "Edit Devices"].map((label) => (
    <div
      key={label}
      className="relative z-10 flex-1 flex flex-col items-center"
    >
      <div className="w-3 h-3 rounded-full bg-green-700 mb-2" />
      <span className="text-sm font-medium text-green-700">
        {label}
      </span>
    </div>
  ))}
</div>

        </div>

        {/* ================= FORM ================= */}
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            {/* ================= DEVICES ================= */}
            <h2 className="text-sm font-semibold mb-6">Devices</h2>

            {devices.map((d, i) => (
              <div key={i} className="mb-10">
                {["deviceName", "deviceId"].map((key) => (
                  <div
                    key={key}
                    className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 mb-4"
                  >
                    <label className="text-sm md:text-right capitalize">
                      {key.replace("device", "Device ")}
                    </label>
                    <input
                      value={d[key] || ""}
                      onChange={(e) =>
                        updateDevice(i, key, e.target.value)
                      }
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                ))}

                <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6">
                  <label className="text-sm md:text-right">Device Info</label>
                  <textarea
                    value={d.deviceInfo || ""}
                    onChange={(e) =>
                      updateDevice(i, "deviceInfo", e.target.value)
                    }
                    className="border rounded-md px-3 py-2 text-sm min-h-[90px]"
                  />
                </div>

                <div className="flex justify-end gap-3 mt-3">
                  <button
                    onClick={() => deleteDevice(d.id, i)}
                    className="bg-red-600 text-white text-xs px-4 py-1 rounded"
                  >
                    Delete
                  </button>
                  <button
                    onClick={addDevice}
                    className="bg-[#4498D8] text-white text-xs px-4 py-1 rounded"
                  >
                    Add One More
                  </button>
                </div>
              </div>
            ))}

            {/* ================= DOSING ================= */}
            <h2 className="text-sm font-semibold text-green-600 mb-6">
              Dosing Device
            </h2>

            {dosingUnits.map((d, i) => (
              <div key={i} className="mb-10">
                {["deviceId", "solutionCapacity", "dripAmount"].map((key) => (
                  <div
                    key={key}
                    className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 mb-4"
                  >
                    <label className="text-sm md:text-right capitalize">
                      {key.replace(/([A-Z])/g, " $1")}
                    </label>
                    <input
                      value={d[key] || ""}
                      onChange={(e) =>
                        updateDosing(i, key, e.target.value)
                      }
                      className="border rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                ))}

                <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6">
                  <label className="text-sm md:text-right">Device Info</label>
                  <textarea
                    value={d.deviceInfo || ""}
                    onChange={(e) =>
                      updateDosing(i, "deviceInfo", e.target.value)
                    }
                    className="border rounded-md px-3 py-2 text-sm min-h-[90px]"
                  />
                </div>

                <div className="flex justify-end gap-3 mt-3">
                  <button
                    onClick={() => deleteDosing(d.id, i)}
                    className="bg-red-600 text-white text-xs px-4 py-1 rounded"
                  >
                    Delete
                  </button>
                  <button
                    onClick={addDosing}
                    className="bg-[#4498D8] text-white text-xs px-4 py-1 rounded"
                  >
                    Add One More
                  </button>
                </div>
              </div>
            ))}

            {/* ================= ACTIONS ================= */}
            <div className="flex justify-end gap-4 mt-10">
              <button
                onClick={() => navigate("/all-users")}
                className="border px-6 py-2 rounded-md text-sm"
              >
                Back
              </button>
              <button
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-md text-sm"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
